<script lang="ts">
    import { DEFAULT_BOX } from '$lib/components/constants';

    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';
    import { getResponseBgColor } from '$lib/utils/getResponseBgColor';

    import Color from 'color';
    import { fade } from 'svelte/transition';
    import CountUp from 'svelte-countup';

    import { isGradientColor, getGradientColors } from '../_utils/color';
    import AnswerIcon from '../_wrappers/AnswerIcon.svelte';
    import Text from '../Text/Text.View.svelte';

    export let id = '';
    export let wysiwygHtml: string | undefined = undefined;
    export let emoji = ':smiley:';
    export let icon = '';
    export let showEmoji = true;
    export let srcType = '';
    export let iconName = '';
    export let platform = '';
    export let iconColor = '#000000';
    export let svg = '';
    export let isAnimated = false;
    export let jsonUrl: string | null = null;
    export let color = 'black';
    export let bgColor = 'transparent';
    export let selectedColor = '#ffffff';
    export let onClick: (answerId: string) => void = () => {};
    export let isResultVisible = false;
    export let responsePercentage: number | undefined = 0;

    let selected = false;
    let checkmarkColor = selectedColor;

    const isGradient = isGradientColor(bgColor);
    const isCheckmarkGradient = isGradientColor(checkmarkColor);

    // We use the first gradient color for checkmarks as secondary color
    if (isCheckmarkGradient) {
        const colors = getGradientColors(checkmarkColor);
        checkmarkColor = colors[0];
    }

    // Prevents white checkmark on too bright BG
    if (Color(checkmarkColor).luminosity() >= 0.8) {
        checkmarkColor = Color(checkmarkColor).darken(0.3).toString();
    }

    const handleClick = () => {
        // Prevent clicks after results are shown
        if (isResultVisible) {
            return;
        }

        selected = !selected;
        onClick(id);
    };

    const handleKeyPress = (event: KeyboardEvent) => {
        event.preventDefault();

        if (event.key === 'Enter' || event.key === ' ') {
            handleClick();
        }
    };

    const bgColorDark = isGradient ? '' : Color(bgColor).darken(0.05);

    const choiceItemBorderRadiusClass = getBorderRadius('choiceItem');

    const backgroundImageStyle = isGradient ? `background-image: ${bgColor};` : '';

    const responseBgColor = getResponseBgColor(bgColor);

    let resultBgWidth = 0;

    $: if (isResultVisible) {
        resultBgWidth = (responsePercentage ?? 0) * 100;
    }
</script>

<div
    class={cn(
        'fun-relative fun-box-border fun-flex fun-min-h-16 fun-w-full fun-cursor-pointer fun-select-none fun-items-center fun-gap-4 fun-overflow-hidden fun-bg-[var(--bgColor)] fun-bg-no-repeat fun-p-4 fun-shadow-text-answer fun-transition-all sm:hover:fun-bg-[var(--bgColorDark)]',
        choiceItemBorderRadiusClass,
        {
            'fun-bg-[length:100%] fun-duration-500 sm:hover:fun-bg-[length:200%]': isGradient,
        },
    )}
    on:click={handleClick}
    on:keypress={handleKeyPress}
    role="button"
    tabindex="0"
    style="--bgColor: {bgColor}; --bgColorDark: {bgColorDark}; {selected
        ? `box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), inset 0 0 0 2px ${checkmarkColor};`
        : ''} {backgroundImageStyle}"
>
    <div
        class={cn(
            'fun-absolute fun-inset-0 fun-z-10 fun-size-full fun-transition-all fun-duration-700 [transition-timing-function:cubic-bezier(0.33,1,0.68,1)]',
            choiceItemBorderRadiusClass,
            responseBgColor,
        )}
        style="width: {resultBgWidth}%;"
    />

    {#if showEmoji}
        <span>
            <AnswerIcon
                {emoji}
                {icon}
                {srcType}
                {iconName}
                {platform}
                {iconColor}
                {svg}
                {isAnimated}
                {jsonUrl}
            />
        </span>
    {/if}
    <span class="fun-flex-grow">
        <Text {wysiwygHtml} {color} box={DEFAULT_BOX} transitionEffect="none" />
    </span>
    {#if isResultVisible}
        <div
            class="fun-font-inter fun-relative fun-z-10 fun-flex fun-h-full fun-items-center fun-justify-center fun-text-2xl fun-font-semibold fun-leading-8 fun-tracking-normal"
            style="color: {color}"
            in:fade
        >
            <CountUp
                initial={0}
                value={((responsePercentage || 0) * 100).toFixed(0)}
                duration={700}
                step={1}
                roundto={1}
            />%
        </div>
    {:else}
        <div
            class="fun-relative fun-size-6 fun-flex-shrink-0 fun-rounded-full fun-bg-white fun-shadow-checkbox-inset"
        >
            <div
                class={cn(
                    'fun-absolute fun-inset-0 fun-size-full fun-rounded-full fun-bg-center fun-bg-no-repeat fun-opacity-0 fun-shadow-checkbox-inset fun-transition',
                    { 'fun-opacity-100': selected },
                )}
                style="background-color: {selected
                    ? checkmarkColor
                    : 'transparent'}; background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTEiIGhlaWdodD0iOSIgdmlld0JveD0iMCAwIDExIDkiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTQuMzYyMjEgNi4xNTgzQzQuMTYzMTEgNi4zNTQxMyAzLjg0MjIgNi4zNDg5IDMuNjQ5NTggNi4xNDY3TDIuNTIzMTggNC45NjQyOEMyLjMyMDUyIDQuNzUxNTQgMS45Nzg5OCA0Ljc1ODU3IDEuNzg1MjUgNC45Nzk0N0wxLjQyMzE4IDUuMzkyMjlDMS4yNDk0NiA1LjU5MDM2IDEuMjU5MjQgNS44ODkyNCAxLjQ0NTUzIDYuMDc1NTNMMy42NDQzOSA4LjI3NDM5QzMuODQwNDUgOC40NzA0NSA0LjE1ODYyIDguNDY5NTMgNC4zNTM1NCA4LjI3MjMzTDEwLjIzMDUgMi4zMjY3QzEwLjQyMzggMi4xMzExMSAxMC40MjI5IDEuODE2MTEgMTAuMjI4NCAxLjYyMTY1TDkuOTU3NCAxLjM1MDYzQzkuNzYzMjkgMS4xNTY1MiA5LjQ0ODk2IDEuMTU1MjEgOS4yNTMyNCAxLjM0NzcxTDQuMzYyMjEgNi4xNTgzWiIgZmlsbD0id2hpdGUiIHN0cm9rZT0id2hpdGUiLz48L3N2Zz4=);"
            >
                &nbsp;
            </div>
        </div>
    {/if}
</div>
